import format from 'date-fns/format';
import {
  type EmailComposePayload,
  buildEmailBodyPreview,
  interpolateMissiveResponseHtml,
  plainTextToHtml,
  resolveEmailDraftBody,
} from 'lib/missive/email-compose';
import type { EmailConversationState } from 'lib/missive/email-session';
import type { MissiveSavedResponse } from 'lib/missive/responses';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useState } from 'react';

import { useSession } from 'providers/session';

const Modal = dynamic(() => import('components/molecules/modal'));

type LeadEmailComposeModalProps = {
  open: boolean;
  email: string;
  leadName?: string;
  onClose: () => void;
  onOpenExisting: () => void;
  onSubmit: (payload: EmailComposePayload) => void;
};

export default function LeadEmailComposeModal({
  open,
  email,
  leadName,
  onClose,
  onOpenExisting,
  onSubmit,
}: LeadEmailComposeModalProps) {
  const { token } = useSession();
  const [selectedResponseId, setSelectedResponseId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [bodyEdited, setBodyEdited] = useState(false);
  const [responses, setResponses] = useState<MissiveSavedResponse[]>([]);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [responsesError, setResponsesError] = useState<string | null>(null);
  const [conversationState, setConversationState] =
    useState<EmailConversationState | null>(null);
  const [conversationLoading, setConversationLoading] = useState(false);

  const selectedResponse = useMemo(
    () =>
      responses.find((response) => response.id === selectedResponseId) || null,
    [responses, selectedResponseId],
  );

  const responseVariables = useMemo(() => {
    const parts = leadName?.trim().split(/\s+/) || [];

    return {
      firstName: parts[0] || undefined,
      lastName: parts.slice(1).join(' ') || undefined,
      email,
    };
  }, [leadName, email]);

  useEffect(() => {
    if (!open || !token || !email.trim()) {
      return;
    }

    setConversationLoading(true);

    fetch(
      `/api/missive/email-session?email=${encodeURIComponent(email.trim())}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )
      .then((response) => response.json())
      .then((payload) => {
        setConversationState(payload);
      })
      .catch(() => {
        setConversationState(null);
      })
      .finally(() => {
        setConversationLoading(false);
      });
  }, [open, email, token]);

  useEffect(() => {
    if (!open || !token) {
      return;
    }

    setResponsesLoading(true);
    setResponsesError(null);

    fetch('/api/missive/responses?team=admissions', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(async (response) => {
        const payload = await response.json();

        if (!response.ok) {
          throw new Error(
            payload?.error?.message ||
              'Failed to load Missive saved responses.',
          );
        }

        setResponses(payload.responses || []);
      })
      .catch((error: Error) => {
        setResponses([]);
        setResponsesError(error.message);
      })
      .finally(() => {
        setResponsesLoading(false);
      });
  }, [open, token]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (responses.length > 0 && !selectedResponseId) {
      setSelectedResponseId(responses[0].id);
    }
  }, [open, responses, selectedResponseId]);

  useEffect(() => {
    if (!open || !selectedResponse) {
      return;
    }

    const interpolatedHtml = interpolateMissiveResponseHtml(
      selectedResponse.bodyHtml,
      responseVariables,
    );

    setSubject(selectedResponse.subject?.trim() || selectedResponse.title);
    setBodyHtml(interpolatedHtml);
    setBody(
      buildEmailBodyPreview(selectedResponse.bodyHtml, responseVariables),
    );
    setBodyEdited(false);
  }, [open, selectedResponse?.id, responseVariables, selectedResponse]);

  useEffect(() => {
    if (!open) {
      setSelectedResponseId('');
      setSubject('');
      setBody('');
      setBodyHtml('');
      setBodyEdited(false);
      setResponses([]);
      setResponsesError(null);
      setConversationState(null);
    }
  }, [open]);

  const canSubmit = body.trim().length > 0;

  function handleSubmit() {
    if (!canSubmit) {
      return;
    }

    const draftHtml = resolveEmailDraftBody(body, bodyHtml, bodyEdited);

    onSubmit({
      mode: 'draft',
      subject: subject.trim() || undefined,
      body: bodyEdited ? body.trim() : undefined,
      bodyHtml: draftHtml || plainTextToHtml(body.trim()),
      conversationId: conversationState?.conversationId || undefined,
    });
  }

  const lastActivityLabel =
    conversationState?.lastActivityAt &&
    format(new Date(conversationState.lastActivityAt * 1000), 'do MMM yyyy p');

  return (
    <Modal
      open={open}
      close={onClose}
      title="Email in Missive"
      subTitle={`Send to ${email}`}
      small={false}
      large
      overflow="overflow-hidden"
    >
      <div className="mt-4 flex max-h-[calc(100vh-11rem)] min-h-0 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="space-y-4 pb-4">
            {conversationLoading ? (
              <p className="text-sm text-gray-500">
                Checking for existing conversation…
              </p>
            ) : conversationState?.hasConversation ? (
              <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-3">
                <p className="text-sm font-medium text-blue-900">
                  Existing email conversation
                </p>
                {conversationState.subject ? (
                  <p className="mt-1 text-sm text-blue-800">
                    {conversationState.subject}
                  </p>
                ) : null}
                {lastActivityLabel ? (
                  <p className="mt-1 text-xs text-blue-700">
                    Last activity {lastActivityLabel}
                  </p>
                ) : null}
                <button
                  type="button"
                  onClick={onOpenExisting}
                  className="mt-3 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-500"
                >
                  Open conversation in Missive
                </button>
              </div>
            ) : (
              <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                No existing Missive thread found. Compose a new email below.
              </p>
            )}

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Saved response
              </label>
              <select
                value={selectedResponseId}
                onChange={(event) => setSelectedResponseId(event.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                disabled={responsesLoading || responses.length === 0}
              >
                {responses.length === 0 ? (
                  <option value="">No Admissions responses</option>
                ) : (
                  responses.map((response) => (
                    <option key={response.id} value={response.id}>
                      {response.title}
                    </option>
                  ))
                )}
              </select>
              {responsesLoading ? (
                <p className="mt-2 text-xs text-gray-500">
                  Loading Missive responses shared with Admissions…
                </p>
              ) : null}
              {responsesError ? (
                <p className="mt-2 text-xs text-amber-700">{responsesError}</p>
              ) : null}
              {!responsesLoading &&
              !responsesError &&
              responses.length === 0 ? (
                <p className="mt-2 text-xs text-amber-700">
                  No saved responses are shared with Admissions in Missive yet.
                </p>
              ) : null}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Subject
              </label>
              <input
                type="text"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                placeholder="A message from Cosmetic College"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Message
              </label>
              <textarea
                value={body}
                onChange={(event) => {
                  setBody(event.target.value);
                  setBodyEdited(true);
                }}
                rows={8}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
              />
              {selectedResponse && !bodyEdited ? (
                <p className="mt-1 text-xs text-gray-500">
                  Loaded from Missive Admissions. Edit here to customise before
                  opening the draft.
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex flex-shrink-0 justify-end gap-2 border-t border-gray-100 bg-white pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="rounded-md bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Open draft in Missive
          </button>
        </div>
      </div>
    </Modal>
  );
}
